import {
	IconAlertTriangle,
	IconAlignLeft,
	IconCalendar,
	IconCalendarCheck,
	IconCalendarOff,
	IconCategory,
	IconClipboardText,
	IconNumber123,
	IconRobotFace,
	IconServer,
	IconToggleRight,
	IconUser,
} from "@tabler/icons-react";
import { Alert, ConfigProvider, Space, Tree, Typography, theme } from "antd";
import type { DataNode } from "antd/es/tree";
import { format } from "date-fns";
import { useAgents } from "@/common/artemis-operations/agents";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import type { CoverageStatus } from "@/common/artemis-sdk";
import type { Approval, ApprovalStatus } from "@/common/model";
import { BaseContainer, IdField } from "../base";
import { BaseDetails } from "../base-details";
import {
	ApprovalRiskBadge,
	ApprovalStatusBadge,
	approvalTypeLabelMap,
} from "./approval-badges";

/** Pull AWS ARNs out of the free-text description so the concrete targets aren't buried in prose. */
export function extractResources(text: string): string[] {
	const matches = text.match(/arn:aws:[^\s,)"]+/gi) ?? [];
	return Array.from(new Set(matches.map((m) => m.replace(/[.,;]+$/, ""))));
}

type Statement = { actions: string[]; resources: string[] };

const groupTitle = (label: string, count: number) => (
	<Typography.Text
		strong
		type="secondary"
		style={{ fontSize: "0.7rem", textTransform: "uppercase" }}
	>
		{label} ({count})
	</Typography.Text>
);

const leafTitle = (item: string) => (
	<span style={{ fontFamily: "monospace", fontSize: "0.75rem" }} title={item}>
		{item}
	</span>
);

/**
 * The requested access as a collapsible tree, one branch per IAM statement: each statement's actions
 * applied to its own resources ("actions ON resources"). Statements are scoped independently — an
 * action pinned to a specific ARN in one statement does not leak onto another's, matching what
 * actually gets minted. Resource lists (long ARNs) collapse by default to save space.
 */
function RequestedAccess({ statements }: { statements: Statement[] }) {
	const {
		token: { colorWarningBg, colorWarningBorder, borderRadius, paddingSM },
	} = theme.useToken();
	const nonEmpty = statements.filter(
		(s) => s.actions.length > 0 || s.resources.length > 0,
	);
	if (nonEmpty.length === 0) return null;

	const multi = nonEmpty.length > 1;
	const treeData: DataNode[] = nonEmpty.flatMap((s, i) => {
		const groups: DataNode[] = [];
		const push = (key: string, label: string, items: string[]) => {
			if (items.length === 0) return;
			groups.push({
				key,
				selectable: false,
				title: groupTitle(label, items.length),
				children: items.map((item, j) => ({
					key: `${key}-${j}`,
					selectable: false,
					title: leafTitle(item),
				})),
			});
		};
		push(`s${i}-a`, "Requested actions", s.actions);
		push(`s${i}-r`, "On resources", s.resources);
		if (!multi) return groups;
		return [
			{
				key: `s${i}`,
				selectable: false,
				title: `Statement ${i + 1}`,
				children: groups,
			},
		];
	});

	return (
		<div style={{ marginTop: 12 }}>
			<Typography.Text
				strong
				type="secondary"
				style={{ fontSize: "0.7rem", textTransform: "uppercase" }}
			>
				Requested access
			</Typography.Text>
			<div
				style={{
					marginTop: 6,
					padding: paddingSM,
					background: colorWarningBg,
					border: `1px solid ${colorWarningBorder}`,
					borderRadius,
				}}
			>
				<ConfigProvider
					theme={{ components: { Tree: { colorBgContainer: "transparent" } } }}
				>
					<Tree
						treeData={treeData}
						selectable={false}
						defaultExpandAll
						blockNode
					/>
				</ConfigProvider>
			</div>
		</div>
	);
}

/**
 * The agent's stated reason, from the frozen payload rather than the prose description. It used to be
 * interpolated into `actionDescription` as well, which is what produced the doubled period approvers
 * saw ("...messaging resources.. Permissions check: ...").
 */
function StatedReason({ reason }: { reason: string }) {
	return (
		<div style={{ marginTop: 12 }}>
			<Typography.Text
				strong
				type="secondary"
				style={{ fontSize: "0.7rem", textTransform: "uppercase" }}
			>
				Stated reason
			</Typography.Text>
			<Typography.Paragraph style={{ margin: "4px 0 0", fontSize: "0.85rem" }}>
				{reason}
			</Typography.Paragraph>
		</div>
	);
}

/**
 * Whether the platform could confirm the environment's Max Role actually allows the requested actions.
 * Anything other than COVERED means no automatic approval was possible (ADR-052), so the person
 * reading this is the only remaining gate — say so plainly rather than leaving it in prose.
 */
function CoverageVerdict({ coverage }: { coverage: CoverageStatus }) {
	if (coverage === "COVERED") {
		return (
			<Alert
				type="success"
				showIcon
				style={{ marginTop: 12 }}
				message="Permissions verified"
				description="AWS policy simulation confirms this environment's Max Role allows every requested action."
			/>
		);
	}

	return (
		<Alert
			type="warning"
			showIcon
			icon={<IconAlertTriangle size={16} />}
			style={{ marginTop: 12 }}
			message="Permissions could not be verified"
			description={
				"The platform could not confirm this environment's Max Role allows these actions, so the " +
				"request was not auto-approved. If you approve it, the agent may still be denied at first " +
				"use. A Max Role that is missing the iam:SimulatePrincipalPolicy grant is the usual cause."
			}
		/>
	);
}

export function ApprovalDetails({ approval }: { approval: Approval }) {
	const { data: environments } = useEnvironments(approval?.workspaceId ?? "");
	const orgSlug = useRouteOrgSlug();
	const { data: agents } = useAgents(approval?.workspaceId ?? "");
	const {
		token: { colorFillTertiary, borderRadius, padding },
	} = theme.useToken();

	if (!approval) return null;
	const environment = environments?.find(
		(e) => e?.id === approval.environmentId,
	);
	const agent = agents?.find((a) => a?.id === approval.agentId);
	// Prefer the frozen structured payload (credential requests carry the exact requested IAM
	// statements — actions scoped to resources). For other request types, fall back to scraping
	// ARNs out of the prose as a single resources-only statement.
	const credentialRequest = approval.credentialRequest;
	const statements: Statement[] = credentialRequest?.statements ?? [
		{
			actions: [],
			resources: extractResources(approval.actionDescription ?? ""),
		},
	];

	return (
		<Space vertical style={{ width: "100%" }} size="large">
			{approval.actionDescription && (
				<BaseContainer size="small" title="Agent's request">
					<div
						style={{
							padding,
							background: colorFillTertiary,
							borderRadius,
						}}
					>
						<Typography.Paragraph
							style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
						>
							{approval.actionDescription}
						</Typography.Paragraph>
					</div>
					{credentialRequest?.reason && (
						<StatedReason reason={credentialRequest.reason} />
					)}
					<RequestedAccess statements={statements} />
					{credentialRequest && (
						<CoverageVerdict coverage={credentialRequest.coverage} />
					)}
				</BaseContainer>
			)}

			<BaseDetails
				item={approval as Approval & Record<string, unknown>}
				fields={[
					{
						key: "id",
						icon: IconNumber123,
						tooltip: "Approval ID",
						render: (id) => <IdField id={id as string} showCopyButton />,
					},
					{
						key: "status",
						icon: IconToggleRight,
						tooltip: "Status",
						render: (status) => (
							<ApprovalStatusBadge status={status as ApprovalStatus} />
						),
					},
					{
						key: "riskLevel",
						icon: IconAlertTriangle,
						tooltip: "Risk",
						render: () => <ApprovalRiskBadge status={approval.riskLevel} />,
					},
					{
						key: "requestType",
						icon: IconCategory,
						tooltip: "Type",
						render: () => approvalTypeLabelMap[approval.requestType],
					},
					{
						key: "taskId",
						icon: IconClipboardText,
						tooltip: "Task",
						render: (id) => (
							<IdField
								id={id as string}
								link={`/${orgSlug}/workspaces/${approval.workspaceId}/tasks/${id}`}
							/>
						),
					},
					{
						key: "agentId",
						icon: IconRobotFace,
						tooltip: "Agent",
						render: () => (
							<IdField
								id={agent?.name}
								link={`/${orgSlug}/workspaces/${approval?.workspaceId}/agents/${approval?.agentId}`}
							/>
						),
					},
					{
						key: "environmentId",
						icon: IconServer,
						tooltip: "Environment",
						render: (id) => (
							<IdField
								id={environment?.name}
								link={`/${orgSlug}/workspaces/${approval.workspaceId}/environments/${id}`}
							/>
						),
					},
					{
						key: "createdAt",
						icon: IconCalendar,
						tooltip: "Created",
						render: (v) =>
							v ? format(new Date(v as string), "MM/dd/yyyy h:mm a") : "-",
					},
					{
						key: "approvalWindowExpiresAt",
						icon: IconCalendarOff,
						tooltip: "Expires",
						render: (v) =>
							v ? format(new Date(v as string), "MM/dd/yyyy h:mm a") : "-",
					},
					{
						key: "resolvedBy",
						icon: IconUser,
						tooltip: "Resolved by",
						render: (id) => <IdField id={id as string} />,
						hidden: (v) => !v,
					},
					{
						key: "resolvedAt",
						icon: IconCalendarCheck,
						tooltip: "Resolved at",
						hidden: (v) => !v,
						render: (v) =>
							v ? format(new Date(v as string), "MM/dd/yyyy h:mm a") : "-",
					},
					{
						key: "notes",
						icon: IconAlignLeft,
						tooltip: "Notes",
						hidden: (v) => !v,
					},
				]}
			/>
		</Space>
	);
}
